import React, { useEffect, useState } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { Button, Checkbox } from "@mui/material";
import dayjs from "dayjs";
import DateTimeModal from "../Schedule/ScheduleListAdd/DateTimeModal";
import { TbFilterEdit } from "react-icons/tb";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import EditMetricModal from "../../components/Matric/AddEditMatric";
import { BiImport } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import DeleteModal from "../../components/Model/DeleteModal";
import Modal from "@mui/material/Modal";

const MatricView = () => {
  const dateFilterData = JSON.parse(localStorage.getItem("metricDateFilter"));
  const [search, setSearch] = useState("");
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openDateModal, setOpenDateModal] = useState(false);
  const [appliedStartDate, setAppliedStartDate] = useState(
    dateFilterData?.startDate || dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [appliedEndDate, setAppliedEndDate] = useState(
    dateFilterData?.endDate || dayjs().endOf("month").format("YYYY-MM-DD")
  );
  const [startDate, setStartDate] = useState(
    dateFilterData?.startDate || dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    dateFilterData?.endDate || dayjs().endOf("month").format("YYYY-MM-DD")
  );
  const [selectedRadio, setSelectedRadio] = useState(
    dateFilterData?.selectedRadio || "month"
  );

  // selection state like bucket
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedId, setSelectedId] = useState([]);
  const [allIds, setAllIds] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });
  const [rowCount, setRowCount] = useState(0);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [showImportMessageModal, setShowImportMessageModal] = useState(false);

  const httpService = new HttpService();

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search,
      };
      const res = await httpService.get("matric", params);
      const list = res?.data?.data || res?.data || [];
      setRowCount(res?.data?.pagination?.total || 0);
      const mapped = Array.isArray(list)
        ? list.map((row, idx) => ({
            id: row.id || idx + 1,
            metricId: row.matric_id,
            dateOfImport: row.date_of_import
              ? dayjs(row.date_of_import).format("DD-MM-YYYY")
              : row.dateOfImport || "",
            assigned:
              typeof row.assigned === "boolean"
                ? row.assigned
                : !!row.is_assigned,
          }))
        : [];
      setMetrics(mapped);
    } catch (e) {
      setMetrics([]);
      setRowCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMetrics();
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    search,
    appliedStartDate,
    appliedEndDate,
  ]);

  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await httpService.post("matric/upload", formData);
      if (response?.success) {
        toast.success("File imported successfully");
        fetchMetrics();
      }
      setImportMessage(
        response?.message ||
          (response?.success ? "File imported successfully" : "Import failed")
      );
      setShowImportMessageModal(true);
    } catch (error) {
      setImportMessage("Import failed. Please try again.");
      setShowImportMessageModal(true);
      toast.error("Import failed. Please try again.");
      // eslint-disable-next-line no-console
      console.error("Import failed:", error);
    }
  };

  const handleDeleteMultiple = async () => {
    if (!selectedId || selectedId.length === 0) return;
    setLoading(true);
    try {
      const res = await httpService.post("matric/deleteMultiple", {
        ids: selectedId,
      });
      if (res?.success) {
        toast.success(res?.message || "Deleted successfully");
        setSelectedId([]);
        setIsAllSelected(false);
        setAllIds(0);
        fetchMetrics();
      } else {
        toast.error(res?.message || "Delete failed.");
      }
    } catch (e) {
      toast.error("Delete failed.");
    }
    setLoading(false);
    setOpenDeleteModal(false);
  };

  const isIdSelected = (id) => {
    return selectedId?.includes(id);
  };

  const toggleId = (id) => {
    setSelectedId((prev = []) => {
      if (prev.includes(id)) {
        const updated = prev.filter((item) => item !== id);
        if (updated.length < allIds) {
          setIsAllSelected(false);
        }
        return updated;
      } else {
        const updated = [...prev, id];
        if (updated.length === allIds) {
          setIsAllSelected(true);
        }
        return updated;
      }
    });
  };

  const handleHeaderCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      const ids = metrics?.map((row) => row.id) || [];
      setSelectedId(ids);
      setAllIds(ids.length);
    } else {
      setSelectedId([]);
      setAllIds(0);
    }
    setIsAllSelected(isChecked);
  };

  const handleEdit = (metric) => {
    setEditData(metric);
    setShowEditModal(true);
  };

  const handleUpdate = (updatedMetric) => {
    setMetrics(
      metrics.map((m) => (m.id === updatedMetric.id ? updatedMetric : m))
    );
    setEditData(null);
  };

  const handleOpenDeletePrompt = () => {
    if (!selectedId || selectedId.length === 0) {
      toast.error("Please select at least one record to delete");
      return;
    }
    setOpenDeleteModal(true);
  };

  const columns = [
    {
      field: "select_All",
      headerName: "Select All",
      minWidth: 150,
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Checkbox
            value="Checked"
            checked={
              isAllSelected ? true : isIdSelected(params.row.id) ? true : false
            }
            onChange={() => toggleId(params.row.id)}
          />
        );
      },
      renderHeader: () => {
        return (
          <>
            <Checkbox
              checked={isAllSelected}
              onChange={(e) => handleHeaderCheckboxChange(e)}
            />
            <strong>Select All</strong>
          </>
        );
      },
    },
    { field: "metricId", headerName: "METRC ID", flex: 1, minWidth: 250 },
    {
      field: "dateOfImport",
      headerName: "Date of Import",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "assigned",
      headerName: "Assigned",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    // action column intentionally commented
  ];

  return (
    <div className="clone">
      <div className="page-title">
        <div className="title-wrap">
          <h1>METRC ID From METRC</h1>
        </div>
        <div className="button-wrap" style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <BiImport width={16} height={16} /> Import
          </button>
        </div>
      </div>

      <div className="table-filter-wrap d-flex justify-content-between pb-0">
        <div className="header_search-wrap">
          <div className="header_search_bar common_content d-flex">
            <div className="input-group input_group">
              <button className="btn search-btn search_btn">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 21 21"
                  fill="none"
                >
                  <circle
                    cx="9.98856"
                    cy="9.98856"
                    r="8.98856"
                    stroke="#495057"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.2402 16.7071L19.7643 20.222"
                    stroke="#495057"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <input
                type="search"
                className="form-control form_control"
                placeholder="Search..."
                aria-label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* <Button
          variant="outlined"
          color="primary"
          onClick={() => setOpenDateModal(true)}
          className="filters-btn"
          style={{ textTransform: "none" }}
        >
          <TbFilterEdit /> Filters
        </Button> */}
      </div>
      <div className="pt-2">
        <Button className="btn btn-delete" onClick={handleOpenDeletePrompt}>
          Delete
        </Button>
      </div>

      <div style={{ width: "100%", marginTop: 24 }}>
        <DataGrid
          rows={metrics}
          columns={columns}
          getRowHeight={() => "auto"}
          loading={loading}
          sx={{
            border: "1px solid #E0E0E0",
            [`& .${gridClasses.cell}`]: { py: 0.5 },
          }}
          autoHeight
          showCellVerticalBorder
          showColumnVerticalBorder
          pagination
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[15, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </div>

      {openDateModal && (
        <DateTimeModal
          handleDateTimeModal={() => setOpenDateModal(false)}
          setStartDate={(e) => setStartDate(e)}
          setEndDate={(e) => setEndDate(e)}
          open={openDateModal}
          fetchSchedule={() => {
            setAppliedStartDate(startDate);
            setAppliedEndDate(endDate);
            const metricDateFilter = {
              startDate: startDate,
              endDate: endDate,
              selectedRadio: selectedRadio,
            };
            localStorage.setItem(
              "metricDateFilter",
              JSON.stringify(metricDateFilter)
            );
          }}
          selectedRadio={selectedRadio}
          setSelectedRadio={(e) => setSelectedRadio(e)}
          startDate={startDate}
          endDate={endDate}
        />
      )}
      <EditMetricModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditData(null);
        }}
        editData={editData}
        onUpdate={handleUpdate}
        showImportModal={showModal}
        onCloseImportModal={() => setShowModal(false)}
        onImport={handleImport}
      />
      {showImportMessageModal && (
        <Modal
          open={showImportMessageModal}
          onClose={() => {
            setShowImportMessageModal(false);
            setImportMessage("");
          }}
          aria-labelledby="import-message-modal-title"
          aria-describedby="import-message-modal-desc"
        >
          <div
            style={{
              padding: 32,
              background: "#fff",
              borderRadius: 8,
              maxWidth: 420,
              margin: "120px auto",
              outline: "none",
              boxShadow: "0 8px 32px rgba(20,40,90,.16)",
              textAlign: "center",
            }}
          >
            <h2
              id="import-message-modal-title"
              style={{ marginBottom: 16, fontSize: 20 }}
            >
              Import METRC
            </h2>
            <div
              id="import-message-modal-desc"
              style={{ marginBottom: 28, fontSize: 16, color: "#1976d2" }}
            >
              {importMessage
                ? importMessage.split(",").map((line, index) => (
                    <div key={index} style={{ marginBottom: index < importMessage.split(",").length - 1 ? 8 : 0 }}>
                      {line.trim()}
                    </div>
                  ))
                : importMessage}
            </div>
            <button
              className="btn btn-primary"
              style={{ minWidth: 120 }}
              onClick={() => {
                setShowImportMessageModal(false);
                setImportMessage("");
              }}
            >
              OK
            </button>
          </div>
        </Modal>
      )}
      {openDeleteModal && (
        <DeleteModal
          open={openDeleteModal}
          HandleClose={() => setOpenDeleteModal(false)}
          HandleDelete={handleDeleteMultiple}
        />
      )}
    </div>
  );
};

export default MatricView;
